import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

export interface GuideBotAvatar3DProps {
    walking: boolean;
}

/**
 * Lightweight WebGL mascot: rounded body, head, eyes, and a solar “visor” hat with cell highlights.
 * Renders in a small square; parent controls size via CSS.
 */
export const GuideBotAvatar3D: React.FC<GuideBotAvatar3DProps> = ({ walking }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const walkingRef = useRef(walking);
    walkingRef.current = walking;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const width = Math.max(1, container.clientWidth);
        const height = Math.max(1, container.clientHeight);

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setClearColor(0x000000, 0);
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.outputEncoding = THREE.sRGBEncoding;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(40, width / height, 0.08, 40);
        camera.position.set(0, 0.1, 1.72);
        camera.lookAt(0, 0.08, 0);

        scene.add(new THREE.AmbientLight(0xffffff, 0.62));
        const key = new THREE.DirectionalLight(0xffffff, 0.95);
        key.position.set(2.2, 3.5, 2.8);
        scene.add(key);
        const rim = new THREE.DirectionalLight(0x93c5fd, 0.35);
        rim.position.set(-2, 1.5, -1.5);
        scene.add(rim);

        const bot = new THREE.Group();

        const bodyMat = new THREE.MeshStandardMaterial({
            color: 0x2563eb,
            metalness: 0.22,
            roughness: 0.48,
        });
        const accentMat = new THREE.MeshStandardMaterial({
            color: 0x1d4ed8,
            metalness: 0.35,
            roughness: 0.38,
            emissive: 0x172554,
            emissiveIntensity: 0.12,
        });

        const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.17, 0.26, 6, 14), bodyMat);
        body.position.y = -0.03;
        bot.add(body);

        const head = new THREE.Mesh(new THREE.SphereGeometry(0.15, 22, 18), accentMat);
        head.position.y = 0.26;
        bot.add(head);

        const eyeMat = new THREE.MeshStandardMaterial({ color: 0x020617, roughness: 0.25, metalness: 0.05 });
        const eyeGeo = new THREE.SphereGeometry(0.032, 10, 10);
        const eyeL = new THREE.Mesh(eyeGeo, eyeMat);
        eyeL.position.set(-0.042, 0.28, 0.125);
        const eyeR = new THREE.Mesh(eyeGeo, eyeMat);
        eyeR.position.set(0.042, 0.28, 0.125);
        bot.add(eyeL, eyeR);

        const cheek = new THREE.Mesh(
            new THREE.SphereGeometry(0.05, 12, 12),
            new THREE.MeshStandardMaterial({
                color: 0x38bdf8,
                emissive: 0x0ea5e9,
                emissiveIntensity: 0.25,
                transparent: true,
                opacity: 0.85,
            })
        );
        cheek.position.set(0, 0.21, 0.118);
        cheek.scale.set(1.4, 0.35, 0.4);
        bot.add(cheek);

        // Solar panel hat / visor
        const hat = new THREE.Group();
        hat.position.set(0, 0.36, 0.04);
        hat.rotation.x = THREE.MathUtils.degToRad(-14);

        const frame = new THREE.Mesh(
            new THREE.BoxGeometry(0.48, 0.055, 0.32),
            new THREE.MeshStandardMaterial({ color: 0x0c4a6e, metalness: 0.55, roughness: 0.32 })
        );
        hat.add(frame);

        const cellMat = new THREE.MeshStandardMaterial({
            color: 0x1e3a8a,
            emissive: 0x38bdf8,
            emissiveIntensity: 0.55,
            metalness: 0.45,
            roughness: 0.22,
        });
        for (let col = 0; col < 3; col++) {
            for (let row = 0; row < 2; row++) {
                const cell = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.018, 0.1), cellMat);
                cell.position.set(-0.125 + col * 0.125, 0.028, -0.055 + row * 0.11);
                hat.add(cell);
            }
        }

        const rail = new THREE.Mesh(
            new THREE.BoxGeometry(0.5, 0.012, 0.04),
            new THREE.MeshStandardMaterial({ color: 0xfacc15, emissive: 0xfbbf24, emissiveIntensity: 0.35 })
        );
        rail.position.set(0, -0.02, 0.14);
        hat.add(rail);

        bot.add(hat);
        scene.add(bot);

        let rafId = 0;
        const t0 = performance.now();
        const tick = (now: number) => {
            rafId = requestAnimationFrame(tick);
            const t = (now - t0) / 1000;
            const w = walkingRef.current;
            bot.rotation.y = Math.sin(t * 1.05) * 0.14;
            bot.position.y = w ? Math.sin(t * 10.5) * 0.045 : Math.sin(t * 2.4) * 0.015;
            hat.rotation.z = Math.sin(t * 1.8) * 0.04;
            renderer.render(scene, camera);
        };
        rafId = requestAnimationFrame(tick);

        container.appendChild(renderer.domElement);

        const ro = new ResizeObserver(() => {
            const w = Math.max(1, container.clientWidth);
            const h = Math.max(1, container.clientHeight);
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
        });
        ro.observe(container);

        return () => {
            cancelAnimationFrame(rafId);
            ro.disconnect();
            if (renderer.domElement.parentNode === container) {
                container.removeChild(renderer.domElement);
            }
            renderer.dispose();
            scene.traverse((obj) => {
                if (obj instanceof THREE.Mesh) {
                    obj.geometry?.dispose();
                    const m = obj.material;
                    if (Array.isArray(m)) m.forEach((x) => x.dispose());
                    else m?.dispose();
                }
            });
        };
    }, []);

    return <div ref={containerRef} className="obd-guide-bot-avatar3d" />;
};

export default GuideBotAvatar3D;
